// Loyola University Chicago: public college page (#2743), official facts only.
//
// Every figure below was read on 2026-10-01 from the source it names:
//  - Loyola's Common Data Set 2025-2026 ("CDS 2025-26 - Loyola University
//    Chicago - Final_af.pdf"), the file the Office of Institutional Research
//    and Analysis links from its Common Data Set page: C1 applicants, admits
//    and enrollees for Fall 2025 with the residency table, C8 test policy for
//    Fall 2027, C21/C22 (no early plans), G1/G5 costs. Quirks: many page
//    footers in the file still read "Common Data Set 2024-2025", though its
//    cover and C1 are 2025-2026 and Fall 2025; the C1 "enrollees by status"
//    table is garbled (it repeats the enrollee counts in the wrong rows), so
//    only the enrollee totals are used; the residency table also has an
//    Unknown column (362 applied, 164 admitted) not shown here; and section
//    G's lead line was edited to read "Provide 2025-2026 academic year
//    costs". The G figures are the 2026-27 ones: the $56,930 tuition and
//    $1,580 fees match Undergraduate Admission's "2026-27 Tuition and Fees"
//    page (luc.edu/undergrad/tuitionscholarshipsandaid/tuitionandfees/),
//    which also lists nursing tuition at $58,180 and room and board
//    averaging $18,572 (the CDS uses the double-room, full-meal-plan rate).
//  - Loyola Undergraduate Admission, First-Year Students (application
//    process, test-optional policy, deadlines). The page carries no cycle
//    year and publishes no decision dates.
//  - College Scorecard, school id 146719 (net price by family income), read
//    from the Scorecard API on 2026-10-01.
// Nothing here is profile-derived, an odds figure or a student record.
// Re-check a figure against its source before editing it, and move
// `lastChecked` when you do.

export const SOURCES = {
    cds: {
        label: 'Loyola Chicago Common Data Set 2025-2026',
        url: 'https://www.luc.edu/oira/institutionaldata/commondataset/',
        read: '2026-10-01',
    },
    firstYear: {
        label: 'Loyola Undergraduate Admission, First-Year Students',
        url: 'https://www.luc.edu/undergrad/admissions/first-yearstudents/',
        read: '2026-10-01',
    },
    scorecard: {
        label: 'College Scorecard, U.S. Department of Education',
        url: 'https://collegescorecard.ed.gov/school/?146719-Loyola-University-Chicago',
        read: '2026-10-15',
    },
};

const college = {
    slug: 'loyola-university-chicago',
    kbId: 'loyola_university_chicago',
    name: 'Loyola University Chicago',
    shortName: 'Loyola Chicago',
    location: 'Chicago, IL',
    control: 'Private',
    lastChecked: '2026-10-01',
    sources: SOURCES,
    // From the KB's sourced branding (Loyola University Marketing and
    // Communication, brand colors); decoration only.
    branding: {
        primary: '#5A0722',
        secondary: '#EAAA00',
        logo_url: 'https://storage.googleapis.com/college-counselor-assets/university-logos/loyola_university_chicago_logo.png',
    },
    faq: {
        answer: { text: 'Loyola University Chicago has no early action or early decision plan: its Common Data Set 2025-2026 answers no to both.', source: 'cds', where: 'sections C21 and C22' },
        questions: [
            { q: 'Does Loyola Chicago have early action?', a: 'No. Loyola has no early action and no early decision; its Common Data Set answers no to both. The December 1 date is a priority deadline, not an early round.', source: 'cds', where: 'sections C21 and C22' },
            { q: 'What is the Loyola Chicago application deadline?', a: 'For fall 2027 entry, the application opens August 1 and the priority deadline is December 1. After that, Loyola keeps admitting students as long as space is available. The enrollment deposit is due May 1, and the admission page lists no decision dates.', source: 'firstYear' },
            { q: 'What is the Loyola Chicago acceptance rate?', a: 'For fall 2025 entry, Loyola admitted 33,009 of 43,954 first-year applicants, an acceptance rate of 75.1%.', source: 'cds', where: 'section C1' },
            { q: 'How many first-year students enroll at Loyola Chicago?', a: 'In fall 2025, 2,605 first-year students enrolled, out of the 33,009 Loyola admitted.', source: 'cds', where: 'section C1' },
            { q: 'What is the Loyola Chicago application fee?', a: 'There is none. You can apply on the Common App or on Loyola\'s own application without paying a fee.', source: 'firstYear' },
        ],
    },

    admission: {
        entering: 'Fall 2025',
        cdsCycle: '2025-2026',
        applied: 43954,
        admitted: 33009,
        enrolled: 2605,
        source: 'cds',
        where: 'section C1',
        residency: [
            { group: 'In-state', applied: 20089, admitted: 15546 },
            { group: 'Out-of-state', applied: 15530, admitted: 13530 },
            { group: 'International', applied: 7973, admitted: 3769 },
        ],
        residencySource: 'cds',
        residencyWhere: 'section C1, residency table (362 applicants and 164 admits of unknown residency not shown)',
    },
    // Acceptance by school or major: shown only where the school publishes it.
    byMajor: {
        published: false,
        note: 'Loyola\'s Common Data Set reports one first-year acceptance rate for the whole university, with no rate by college or major.',
        source: 'cds',
    },
    earlyRounds: {
        title: 'Early plans',
        note: 'Loyola has no early decision or early action plan; the Common Data Set answers no to both. December 1 is a priority deadline, not an early round.',
        source: 'cds',
        where: 'sections C21 and C22',
    },

    deadlines: {
        entering: 'Fall 2027',
        headline: { label: 'Priority deadline', value: 'December 1' },
        rows: [
            { plan: 'Priority application', deadline: 'December 1', decision: null, binding: false },
        ],
        note: 'The application opens August 1, on the Common App or Loyola\'s own application, and there is no application fee. After December 1, Loyola keeps admitting students as long as space is available. The enrollment deposit is due May 1. Loyola does not publish decision dates.',
        source: 'firstYear',
    },

    testing: {
        entering: 'Fall 2027',
        policy: 'Test-optional',
        blind: false,
        line: 'Loyola is test-optional: you decide whether to include ACT or SAT scores. If you send them, Loyola superscores, reviewing your highest score in each section across sittings, and recommends sharing all your results. You may self-report scores; if admitted, you have official scores sent by May 1.',
        source: 'firstYear',
        cdsLine: 'The Common Data Set also marks the SAT or ACT as not required for fall 2027 applicants, but considered if submitted.',
        cdsWhere: 'section C8',
    },

    // The next cycle (2027-28, fall 2028 entry): only what the school has
    // announced, read on the date below; nothing announced says so (#2651).
    nextCycle: {
        entering: 'Fall 2028',
        checked: '2026-10-01',
        changes: [],
        source: 'firstYear',
    },

    cost: {
        year: '2026-2027',
        rows: [
            { item: 'Tuition', amount: 56930 },
            { item: 'Required fees', amount: 1580 },
            { item: 'Housing (on campus)', amount: 11270 },
            { item: 'Food (on-campus meal plan)', amount: 6710 },
            { item: 'Books and supplies', amount: 1600 },
            { item: 'Transportation', amount: 450 },
            { item: 'Other expenses', amount: 1700 },
        ],
        total: 80240,
        note: 'For a full-time first-year student living on campus, the full academic year. Nursing tuition is higher ($58,180 for 2026-27, per Loyola\'s Undergraduate Admission tuition page), and that page puts average room and board at $18,572.',
        source: 'cds',
        where: 'sections G1 and G5',
        netPrice: {
            average: 36340,
            byIncome: [
                { income: '$0 to $30,000', amount: 30709 },
                { income: '$30,001 to $48,000', amount: 31498 },
                { income: '$48,001 to $75,000', amount: 31297 },
                { income: '$75,001 to $110,000', amount: 35289 },
                { income: 'Over $110,000', amount: 43708 },
            ],
            note: 'What first-year, full-time students who received federal aid paid after grants and scholarships, by family income, in 2024-25, the latest year the Department of Education publishes. Your own price comes from Loyola\'s aid offer.',
            tileDetail: 'Per year, after grants, federal aid recipients',
            source: 'scorecard',
        },
    },
};

export default college;
